/**
 * Fixed-position popover portaled to document.body so overflow:hidden parents
 * cannot clip it (same pattern as DateTimePicker / CardLinkPicker).
 */

import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { computeAnchoredPopoverPosition } from './anchoredPopover';

const DEFAULT_Z_INDEX = 10050;

export interface AnchoredPortalProps {
  open: boolean;
  /** Element that owns the popover (trigger). Click-outside ignores this node. */
  anchorRef: React.RefObject<HTMLElement | null>;
  onClose: () => void;
  children: React.ReactNode;
  className?: string;
  role?: string;
  'aria-label'?: string;
  'data-testid'?: string;
  /** Fallback size before first layout measure. */
  estimatedWidth?: number;
  estimatedHeight?: number;
  zIndex?: number;
  gap?: number;
  /** Prefer opening above the trigger (Slack reply bottom chrome). */
  preferAbove?: boolean;
}

export const AnchoredPortal: React.FC<AnchoredPortalProps> = ({
  open,
  anchorRef,
  onClose,
  children,
  className,
  role,
  'aria-label': ariaLabel,
  'data-testid': testId,
  estimatedWidth = 280,
  estimatedHeight = 260,
  zIndex = DEFAULT_Z_INDEX,
  gap = 6,
  preferAbove = false,
}) => {
  const popoverRef = useRef<HTMLDivElement>(null);
  const [coords, setCoords] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  const updatePosition = useCallback(() => {
    const anchor = anchorRef.current;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    const popover = popoverRef.current;
    const width = popover?.offsetWidth || estimatedWidth;
    const height = popover?.offsetHeight || estimatedHeight;
    setCoords(computeAnchoredPopoverPosition(rect, width, height, { gap, preferAbove }));
  }, [anchorRef, estimatedHeight, estimatedWidth, gap, preferAbove]);

  useLayoutEffect(() => {
    if (!open) return;
    updatePosition();
    const popover = popoverRef.current;
    const ro =
      typeof ResizeObserver !== 'undefined' && popover
        ? new ResizeObserver(() => updatePosition())
        : null;
    if (popover && ro) ro.observe(popover);
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (anchorRef.current?.contains(target) || popoverRef.current?.contains(target)) {
        return;
      }
      onClose();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        anchorRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, onClose, anchorRef]);

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <div
      ref={popoverRef}
      className={className}
      role={role}
      aria-label={ariaLabel}
      data-testid={testId}
      style={{
        position: 'fixed',
        top: coords.top,
        left: coords.left,
        zIndex,
      }}
    >
      {children}
    </div>,
    document.body,
  );
};
